'use client';

import { hasLegacyCoveoHash } from '@lib/coveo/legacyHash';
import { COVEO_URL_CHANGE_EVENT } from '@lib/coveo/urlChangeEvent';
import {
  SearchResultsBannerDisplayType,
  SearchResultsBannerDisplayTypePriority,
  SearchResultsBanner as SearchResultsBannerType,
} from '@lib/types/search/SearchResultsBanner';
import c from 'classnames';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Link } from 'ui-components';

type SearchResultsBannerProps = {
  banners: SearchResultsBannerType[];
  initialSaleFilters?: string | null;
};

const isBannerDisplayable = (
  banner: SearchResultsBannerType,
  filtersString: string | null
) => {
  if (banner.displayType === SearchResultsBannerDisplayType.FLASH_DEAL) {
    return filtersString?.includes('flash_deal');
  }

  if (banner.displayType === SearchResultsBannerDisplayType.NEW_RELEASE) {
    return filtersString?.includes('new_release_discount');
  }

  if (banner.displayType === SearchResultsBannerDisplayType.ON_SALE) {
    return filtersString?.includes('on_sale');
  }

  return banner.displayType === SearchResultsBannerDisplayType.DEFAULT;
};

const getActiveBanner = (
  banners: SearchResultsBannerType[],
  filtersString: string | null
) =>
  banners
    .filter((banner) => isBannerDisplayable(banner, filtersString))
    .sort(
      (a, b) =>
        SearchResultsBannerDisplayTypePriority[a.displayType] -
        SearchResultsBannerDisplayTypePriority[b.displayType]
    )[0];

/**
 * Tracks the active `f-ec_sale_filters` selection in the URL. The CSR search
 * page carries it in the hash (comma-joined) and the SSR page in the query
 * string (repeated keys). Whenever the hash carries any legacy Coveo param it
 * is authoritative for the sale filter too, including its ABSENCE: the CSR
 * engine writes its complete state to the hash while leaving the query string
 * verbatim, so a seeded `?f-ec_sale_filters=…` goes stale the moment the user
 * deselects the facet, and falling back to it would stick the banner. The
 * query string only speaks when there is no Coveo hash at all (the SSR page,
 * or a fully migrated URL).
 * The initial value is seeded by the server page from `searchParams` so the
 * SSR document carries the right banner; the hash-based CSR page can't do
 * that and starts at `null`. Updates on browser navigation (`popstate`,
 * `hashchange`) and on in-app history writes broadcast by the Coveo URL
 * managers via {@link COVEO_URL_CHANGE_EVENT} (which native
 * `pushState`/`replaceState` don't emit themselves).
 */
const useSaleFilters = (initialSaleFilters: string | null): string | null => {
  const [saleFilters, setSaleFilters] = useState<string | null>(
    initialSaleFilters
  );
  useEffect(() => {
    const read = () => {
      const fromHash = new URLSearchParams(window.location.hash.slice(1)).get(
        'f-ec_sale_filters'
      );
      const fromQuery = new URLSearchParams(window.location.search)
        .getAll('f-ec_sale_filters')
        .join(',');
      setSaleFilters(hasLegacyCoveoHash() ? fromHash : fromQuery || null);
    };
    read();
    window.addEventListener('popstate', read);
    window.addEventListener('hashchange', read);
    window.addEventListener(COVEO_URL_CHANGE_EVENT, read);
    return () => {
      window.removeEventListener('popstate', read);
      window.removeEventListener('hashchange', read);
      window.removeEventListener(COVEO_URL_CHANGE_EVENT, read);
    };
  }, []);
  return saleFilters;
};

export const SearchResultsBanner = ({
  banners,
  initialSaleFilters = null,
}: SearchResultsBannerProps) => {
  const saleFilter = useSaleFilters(initialSaleFilters);
  const activeBanner = getActiveBanner(banners, saleFilter);

  if (!activeBanner) {
    return null;
  }

  return (
    <Link
      href={activeBanner.link.href}
      target={activeBanner.link.openInNewTab ? '_blank' : undefined}
      className={c('relative mb-6 block w-full md:aspect-[32/5] lg:mb-14', {
        ['aspect-[32/5]']: !activeBanner.mobileImage,
        ['aspect-[49/16]']: activeBanner.mobileImage,
      })}
    >
      <Image
        fill
        sizes="100vw"
        src={activeBanner.image.url}
        alt={activeBanner.image.alt ?? ''}
        className={c('object-cover', {
          ['hidden md:block']: activeBanner.mobileImage,
        })}
      />
      {activeBanner.mobileImage && (
        <Image
          fill
          sizes="100vw, (min-width: 768px) 0vw"
          src={activeBanner.mobileImage.url}
          alt={activeBanner.mobileImage.alt ?? ''}
          className={c('block object-cover md:hidden')}
        />
      )}
    </Link>
  );
};
